import React from 'react'
import { NoData, screenSize } from 'styles/shared'
import Table from 'components/Table'
import SiteMessage from 'pages/shared/SiteMessage'
import Tooltip from 'components/Tooltip'
import styled, { css } from 'styled-components'
import { color } from 'styles/colors'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'
import _ from 'underscore'
import moment from 'moment/moment'
import { SiteMessageDetail } from 'types/SiteMessageDetail'

type Props = {
  filteredSiteMessages: Array<SiteMessageDetail>
  unreadSiteMessageIds: Array<number>
  onClickCallback: (label: string) => void
  onClickAction: (id: number, label: string) => void
  tabSelection: ('Current' | 'Archive')
}

const UpdateDetail = ({ filteredSiteMessages, unreadSiteMessageIds, onClickCallback, onClickAction, tabSelection = 'Current' }: Props) => {
  const sortSiteMessages = (siteMessagesToSort) => {
    return _.chain(siteMessagesToSort)
      .sortBy((siteMessage) => moment(siteMessage.publishedAt).toDate().getTime())
      .reverse()
      .value()
  }

  const onActionButtonClickCallback = () => {
    onClickCallback('Clicked action button')
  }

  const onImageClickedCallback = () => {
    onClickCallback('Clicked image')
  }

  const onTextClickedCallback = () => {
    onClickCallback('Clicked text link')
  }

  return (
    <>
      {filteredSiteMessages.length === 0 && (
        <NoData>
          <h1>No Updates</h1>
        </NoData>
      )}

      {filteredSiteMessages.length > 0 &&
        <>
          <br />
          <Table.Header>
            <S.MessageHeaderCell title="Message" />
            <S.ActionHeaderCell title="Actions" />
          </Table.Header>
          {sortSiteMessages(filteredSiteMessages).map(filteredSiteMessages => (
            <S.MessageRow key={filteredSiteMessages.id}>
              <S.MessageCell>
                <S.SiteMessage key={filteredSiteMessages.id} messageType={filteredSiteMessages.messageType}>
                  <SiteMessage.PublishedDate publishedAt={filteredSiteMessages.publishedAt} />

                  {unreadSiteMessageIds?.includes(filteredSiteMessages.id) && <S.NewLabel>NEW</S.NewLabel>}

                  <SiteMessage.Body
                    showCollapsed={!unreadSiteMessageIds?.includes(filteredSiteMessages.id)}
                    siteMessage={filteredSiteMessages}
                    onActionButtonClickCallback={onActionButtonClickCallback}
                    onImageClickCallback={onImageClickedCallback}
                    onTextClickCallback={onTextClickedCallback}
                  />
                </S.SiteMessage>
              </S.MessageCell>
              <S.ActionTakenCell>
                {tabSelection === 'Current' &&
                  <Tooltip title="Archive Message" placement="top">
                    <S.ArchiveOutlinedIcon
                      role="img"
                      aria-label="Archive Message"
                      onClick={() => onClickAction(filteredSiteMessages.id, 'archive')}
                    />
                  </Tooltip>
                }
                {tabSelection !== 'Current' &&
                  <div>
                    <Tooltip title="Restore Message" placement="top">
                      <S.RestoreOutlinedIcon role="img" aria-label="Restore Message" onClick={() => onClickAction(filteredSiteMessages.id, 'restore')} />
                    </Tooltip>
                    <Tooltip title="Delete Message" placement="top">
                      <S.DeleteOutlinedIcon role="img" aria-label="Delete Message" onClick={() => onClickAction(filteredSiteMessages.id, 'delete')} />
                    </Tooltip>
                  </div>
                }
              </S.ActionTakenCell>
            </S.MessageRow>
          ))}
        </>
      }
    </>
  )
}

export default UpdateDetail

const iconStyle = (iconType) => {
  return css`
    margin: 8px 0 0 10px;
    cursor: pointer;
    color: ${color(iconType)};

    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const MESSAGE_COLUMN_FLEX_VALUE = '4'
const ACTION_COLUMN_FLEX_VALUE = '1'

const S = {
  SiteMessage: styled(SiteMessage)`
    width: 100%;
    margin-bottom: 20px;
    border: 1px solid ${color('border')};
  `,
  NewLabel: styled.div`
    width: 44px;
    height: 21px;
    text-align: center;
    margin-bottom: 7px;
    font-weight: bold;
    border-radius: 5px;
    background: ${color('newLabelBackground')};
    color: ${color('newLabelText')};
  `,
  MessageHeaderCell: styled(Table.HeaderCell)`
    flex: ${MESSAGE_COLUMN_FLEX_VALUE};
  `,
  ActionHeaderCell: styled(Table.HeaderCell)`
    flex: ${ACTION_COLUMN_FLEX_VALUE};
  `,
  MessageRow: styled(Table.Row)`
      padding: 10px 0;

      &:last-child {
          border-bottom: none;
      }

      @media ${screenSize.small} {
          flex-flow: row wrap;
          padding: 10px 0 30px;
      }
  `,
  MessageCell: styled(Table.Cell)`
      flex: ${MESSAGE_COLUMN_FLEX_VALUE};
      flex-direction: column;
      margin-right: 15px;

      @media ${screenSize.small} {
          flex: 1 1 100%;
          margin-right: 0;
          margin-bottom: 15px;
      }
  `,
  ActionTakenCell: styled(Table.Cell)`
      flex: ${ACTION_COLUMN_FLEX_VALUE};
      flex-direction: column;

      @media ${screenSize.small} {
          display: inline-block;
          width: 50%;
          padding-left: 5px;
      }
  `,
  DeleteOutlinedIcon: styled(DeleteOutlinedIcon)`
    ${iconStyle('destructiveIcon')}
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `,
  RestoreOutlinedIcon: styled(RestoreOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `
}
